import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Download, Edit, Save, X, Eye } from 'lucide-react';
import jsPDF from 'jspdf';

interface Patient {
  id?: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  geschlecht: string;
  zimmernummer: string;
  aufnahmedatum: string;
  diagnose: string;
  besonderheiten: string;
}

interface Pflegeplan {
  id?: string;
  patient_id: string;
  titel: string;
  lebensaktivitaet: string;
  ressourcen: string[];
  probleme: string[];
  ziele: string[];
  massnahmen: string[];
}

interface PflegeplanPreviewProps {
  selectedPatient: Patient | null;
  pflegeplan: Pflegeplan;
  setPflegeplan: (pflegeplan: Pflegeplan | ((prev: Pflegeplan) => Pflegeplan)) => void;
  onExportToPDF: () => void;
}

const PflegeplanPreview = ({
  selectedPatient,
  pflegeplan,
  setPflegeplan,
  onExportToPDF
}: PflegeplanPreviewProps) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState('');

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'ressourcen':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'probleme':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'ziele':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'massnahmen':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'ressourcen':
        return 'Ressourcen';
      case 'probleme':
        return 'Probleme';
      case 'ziele':
        return 'Ziele';
      case 'massnahmen':
        return 'Maßnahmen';
      default:
        return category;
    }
  };

  const startEditing = (category: string, index: number, text: string) => {
    setEditingCategory(category);
    setEditingIndex(index);
    setEditText(text);
  };

  const saveEdit = () => {
    if (editingCategory && editingIndex !== null) {
      setPflegeplan(prev => ({
        ...prev,
        [editingCategory]: prev[editingCategory as keyof typeof prev].map((item: string, index: number) => 
          index === editingIndex ? editText : item
        )
      }));
      cancelEdit();
    }
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setEditingIndex(null);
    setEditText('');
  };

  const removeItem = (category: string, index: number) => {
    setPflegeplan(prev => ({
      ...prev,
      [category]: prev[category as keyof typeof prev].filter((_: string, i: number) => i !== index)
    }));
  };

  const exportToPDF = () => {
    if (!selectedPatient || !pflegeplan.titel) {
      return;
    }

    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Helper function to add text with word wrapping
    const addText = (text: string, fontSize: number = 12, isBold: boolean = false) => {
      pdf.setFontSize(fontSize);
      if (isBold) {
        pdf.setFont('helvetica', 'bold');
      } else {
        pdf.setFont('helvetica', 'normal');
      }
      
      const lines = pdf.splitTextToSize(text, pageWidth - 2 * margin);
      pdf.text(lines, margin, yPosition);
      yPosition += lines.length * (fontSize * 0.4) + 5;
      
      // Check if we need a new page
      if (yPosition > pdf.internal.pageSize.getHeight() - margin) {
        pdf.addPage();
        yPosition = margin;
      }
    };

    // Title
    addText('PFLEGEPLANUNG', 20, true);
    yPosition += 10;

    // Patient information
    addText('PATIENTENINFORMATIONEN', 16, true);
    addText(`Name: ${selectedPatient.vorname} ${selectedPatient.nachname}`);
    addText(`Geburtsdatum: ${selectedPatient.geburtsdatum}`);
    addText(`Geschlecht: ${selectedPatient.geschlecht}`);
    addText(`Zimmer: ${selectedPatient.zimmernummer}`);
    addText(`Aufnahmedatum: ${selectedPatient.aufnahmedatum}`);
    addText(`Diagnose: ${selectedPatient.diagnose}`);
    if (selectedPatient.besonderheiten) {
      addText(`Besonderheiten: ${selectedPatient.besonderheiten}`);
    }
    yPosition += 10;

    // Care plan information
    addText('PFLEGEPLAN', 16, true);
    addText(`Titel: ${pflegeplan.titel}`);
    addText(`Lebensaktivität: ${pflegeplan.lebensaktivitaet}`);
    yPosition += 10;

    // Resources
    if (pflegeplan.ressourcen.length > 0) {
      addText('RESSOURCEN', 14, true);
      pflegeplan.ressourcen.forEach((ressource, index) => {
        addText(`${index + 1}. ${ressource}`);
      });
      yPosition += 5;
    }

    // Problems
    if (pflegeplan.probleme.length > 0) {
      addText('PFLEGEPROBLEME', 14, true);
      pflegeplan.probleme.forEach((problem, index) => {
        addText(`${index + 1}. ${problem}`);
      });
      yPosition += 5;
    }

    // Goals
    if (pflegeplan.ziele.length > 0) {
      addText('PFLEGEZIELE', 14, true);
      pflegeplan.ziele.forEach((ziel, index) => {
        addText(`${index + 1}. ${ziel}`);
      });
      yPosition += 5;
    }

    // Measures
    if (pflegeplan.massnahmen.length > 0) {
      addText('PFLEGEMASSNAHMEN', 14, true);
      pflegeplan.massnahmen.forEach((massnahme, index) => {
        addText(`${index + 1}. ${massnahme}`);
      });
      yPosition += 5;
    }

    // Footer
    yPosition += 10;
    addText(`Erstellt am: ${new Date().toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })}`);

    // Save PDF
    pdf.save(`Pflegeplan_${selectedPatient.nachname}_${selectedPatient.vorname}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const CategorySection = ({ 
    title, 
    category, 
    items 
  }: { 
    title: string; 
    category: string; 
    items: string[];
  }) => (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Badge className={getCategoryColor(category)}>
            {title}
          </Badge>
          <span className="text-sm font-normal text-gray-600">({items.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-gray-500 text-sm italic">
            Keine {title.toLowerCase()} definiert.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((item, index) => (
              <div key={index} className="group">
                {editingCategory === category && editingIndex === index ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="min-h-[80px]"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveEdit}>
                        <Save className="h-3 w-3 mr-1" />
                        Speichern
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit}>
                        <X className="h-3 w-3 mr-1" />
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-colors">
                    <span className="flex-1 text-sm">{item}</span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEditing(category, index, item)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeItem(category, index)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (!selectedPatient || !pflegeplan.titel) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Vorschau & Bearbeiten
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Pflegeplan Vorschau & Bearbeitung</DialogTitle>
              <DialogDescription>
                Überprüfen und bearbeiten Sie Ihren Pflegeplan vor dem Export.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Patient Info */}
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-blue-900">Patienteninformationen</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><strong>Name:</strong> {selectedPatient.vorname} {selectedPatient.nachname}</div>
                    <div><strong>Zimmer:</strong> {selectedPatient.zimmernummer}</div>
                    <div><strong>Geburtsdatum:</strong> {selectedPatient.geburtsdatum}</div>
                    <div><strong>Geschlecht:</strong> {selectedPatient.geschlecht}</div>
                    <div className="col-span-2"><strong>Diagnose:</strong> {selectedPatient.diagnose}</div>
                  </div>
                </CardContent>
              </Card>

              {/* Plan Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Pflegeplan</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div><strong>Titel:</strong> {pflegeplan.titel}</div>
                    <div><strong>Lebensaktivität:</strong> {pflegeplan.lebensaktivitaet}</div>
                  </div>
                </CardContent>
              </Card>

              <Separator />

              {/* Editable Categories */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Pflegeplan-Inhalte</h3>
                <p className="text-sm text-gray-600">
                  Klicken Sie auf das Bearbeiten-Symbol, um Formulierungen anzupassen.
                </p>
                
                <CategorySection
                  title="Ressourcen"
                  category="ressourcen"
                  items={pflegeplan.ressourcen}
                />

                <CategorySection
                  title="Probleme"
                  category="probleme"
                  items={pflegeplan.probleme}
                />

                <CategorySection
                  title="Ziele"
                  category="ziele"
                  items={pflegeplan.ziele}
                />

                <CategorySection
                  title="Maßnahmen"
                  category="massnahmen"
                  items={pflegeplan.massnahmen}
                />
              </div>

              {/* Export Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>
                  Schließen
                </Button>
                <Button onClick={exportToPDF} className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Als PDF herunterladen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Button onClick={exportToPDF} className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Direkt als PDF herunterladen
        </Button>
      </div>
    </div>
  );
};

export default PflegeplanPreview;